import { Component, AfterViewInit, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AuthService } from '../../core/service/authentication/auth.service';
import { LoaderService } from '../../core/service/loader/loader.service';
import { Router } from '@angular/router';
import { ApiResponseStatus } from '../../core/models/common/enums/api-response-status.enum';
import ErrorResponse from '../../core/models/common/error-response.model';
import { NotificationService } from '../../core/service/notifications/notification.service';
import { TicketService } from '../../core/service/tickets/tickets.service';
import Ticket from '../../core/models/ticket.model';
import { ApiResponseErrorCodes } from '../../core/models/common/enums/api-response-error-codes.enum';
import User from '../../core/models/user.model';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, AfterViewInit {
  iban = '';
  isIbanValid = false;
  isCurrentPasswordVisible = false;
  isPasswordVisible = false;
  isConfirmPasswordVisible = false;

  pageSize = 10;
  page = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: any[] = []
  yourTicket: Ticket[] = [];
  votedTicket: Ticket[] = [];
  pagination = { currentPage: 1, totalPages: 1 };

  initialInbWallet = '';
  initialOutWallet = '';
  initialEmail = '';
  initalIban = '';

  walletExistsError = false;

  regexValidator = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  changeEmailAddressForm: FormGroup = new FormGroup({})
  changeWalletForm: FormGroup = new FormGroup({})
  changeIbanForm: FormGroup = new FormGroup({})


  changePasswordForm: FormGroup = new FormGroup({
    currentPassword: new FormControl('', {
      validators: [Validators.required],
    }),
    newPassword: new FormControl('', {
      validators: [Validators.required, Validators.pattern(this.regexValidator)],
    }),
    confirmPassword: new FormControl('', {
      validators: [Validators.required, Validators.pattern(this.regexValidator)],
    }),
  },
    {
      validators: [this.passwordMatchValidator()],
    }
  );

  constructor(
    private loaderService: LoaderService,
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService,
    private ticketService: TicketService
  ) { }

  ngOnInit(): void {
    const isLogged = this.authService.isAuthenticated();
    if (!isLogged) {
      this.router.navigateByUrl('');
    }

    this.loadArchivedTickets();

    this.authService.currentUser$.subscribe(user => {
      this.loadProfileForms(user)
    })
  }

  ngAfterViewInit(): void {
    this.loaderService.showLoader();
    this.loaderService.hideLoader();
  }

  updateButtonState(): void {
    this.updateButtonStyle(this.changeEmailAddressForm, 'save-email-button');
    this.updateButtonStyle(this.changePasswordForm, 'save-password-button');
    this.updateButtonStyle(this.changeWalletForm, 'save-wallet-button');
    this.updateButtonStyle(this.changeIbanForm, 'save-iban-button');
  }

  private loadProfileForms(user: User | null): void {

    this.initialInbWallet = user?.incomingWalletHash || '';
    this.initialOutWallet = user?.outcomingWalletHash || '';
    this.initialEmail = user?.emailAddress || '';

    this.initalIban = user?.iban ? '**** ' + user?.iban : '';

    this.changeEmailAddressForm = new FormGroup({
      changeEmailAddress: new FormControl(this.initialEmail, [Validators.required, Validators.email]),
    });

    this.changeWalletForm = new FormGroup({
      inbWallet: new FormControl(this.initialInbWallet, {
        validators: [Validators.required],
      }),
      outWallet: new FormControl(this.initialOutWallet, {
        validators: [Validators.required],
      }),
    }
    );

    this.changeIbanForm = new FormGroup({
      ibanCode: new FormControl(this.initalIban, {
        validators: [Validators.required],
      })
    },
      {
        validators: [this.validateIban()],
      }
    );

    // Listener per cambio stile bottoni
    this.changeEmailAddressForm.statusChanges.subscribe(() => this.updateButtonState());
    this.changePasswordForm.statusChanges.subscribe(() => this.updateButtonState());
    this.changeWalletForm.statusChanges.subscribe(() => this.updateButtonState());
    this.changeIbanForm.statusChanges.subscribe(() => this.updateButtonState());
  }

  get isWalletFormValid(): boolean {
    const currentInb = this.changeWalletForm.get('inbWallet')?.value;
    const currentOut = this.changeWalletForm.get('outWallet')?.value;
    return this.changeWalletForm.valid &&
      (currentInb !== this.initialInbWallet ||
        currentOut !== this.initialOutWallet);
  }

  get isEmailFormValid(): boolean {
    const currentEmail = this.changeEmailAddressForm.get('changeEmailAddress')?.value;
    return this.changeEmailAddressForm.valid &&
      currentEmail !== this.initialEmail &&
      this.changeEmailAddressForm.dirty;
  }

  updateButtonStyle(form: FormGroup, buttonId: string): void {
    const button = document.getElementById(buttonId);
    if (form.valid) {
      button?.classList.add('enabled');
      button?.removeAttribute('disabled');
    } else {
      button?.classList.remove('enabled');
      button?.setAttribute('disabled', 'true');
    }
  }

  toggleCurrentPasswordVisibility(): void {
    this.isCurrentPasswordVisible = !this.isCurrentPasswordVisible;
  }

  toggleNewPasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  toggleConfirmPasswordVisibility(): void {
    this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
  }

  onPageChange(page: number): void {
    this.page = page
    this.loadArchivedTickets();
  }

  /*====== VALIDATORS ========*/

  validateIban(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const iban = form.value.ibanCode;

      if (iban.length < 22 || iban.length > 34) {
        return { ibanMismatch: true };
      }

      const countryCodePattern = /^[A-Z]{2}/;
      if (!countryCodePattern.test(iban)) {
        return { ibanMismatch: true };
      }

      const ibanPattern = /^[A-Z0-9]+$/;
      if (!ibanPattern.test(iban)) {
        return { ibanMismatch: true };
      }

      return null;
    };
  }

  passwordMatchValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const password = form.value.password;
      const confirmPassword = form.value.confirmPassword;

      if (password && confirmPassword && password !== confirmPassword) {
        return { passwordMismatch: true };
      }

      return null;
    };
  }

  /*====== API CALL ========*/

  onSubmitChangePassword(): void {
    const { currentPassword, newPassword } = this.changePasswordForm.value;

    this.loaderService.showLoader();
    this.authService.updatePassword(currentPassword, newPassword).subscribe(
      (response) => {
        this.loaderService.hideLoader();
        if (response && response.status === ApiResponseStatus.Error) {
          const errorResponse = response.content as ErrorResponse;
          console.error(errorResponse);
          this.notificationService.showError('Operation failed');
        } else {
          this.notificationService.showSuccess('Password changed successfully');
          this.changePasswordForm.reset();
        }
      }
    );
  }

  //preview iban change service
  onSubmitChangeIban(): void {
    const { ibanCode } = this.changeIbanForm.value;

    this.loaderService.showLoader();
    this.authService.updateIban(ibanCode).subscribe(
      (response) => {
        this.loaderService.hideLoader();

        if (response && response.status === ApiResponseStatus.Error) {
          const errorResponse = response.content as ErrorResponse;
          console.error(errorResponse);
          this.notificationService.showError('Operation failed');
        } else {
          this.notificationService.showSuccess('IBAN changed successfully');
        }

      }
    );
  }

  onSubmitChangeWallet(): void {
    const { inbWallet, outWallet } = this.changeWalletForm.value;

    this.loaderService.showLoader();
    this.authService.updateWallet(inbWallet, outWallet).subscribe(
      (response) => {
        this.loaderService.hideLoader();

        if (response?.status === ApiResponseStatus.Error) {
          const errorResponse = response.content as ErrorResponse;

          if (errorResponse.type === ApiResponseErrorCodes.AlreadyExistsUserWithSameWallet) {
            this.walletExistsError = true;
          }
        } else {
          this.notificationService.showSuccess('Wallet address changed successfully');
        }
      }
    );
  }


  onSubmitChangeEmail(): void {
    const { changeEmailAddress } = this.changeEmailAddressForm.value;

    this.loaderService.showLoader();
    this.authService.updateEmailAddress(changeEmailAddress).subscribe(
      (response) => {
        this.loaderService.hideLoader();

        if (response && response.status === ApiResponseStatus.Error) {
          const errorResponse = response.content as ErrorResponse;
          console.error(errorResponse);
          this.notificationService.showError('Operation failed');
        } else {
          this.notificationService.showSuccess('A message has been sent to your email address. Please confirm it before start to use GoGo Money Maker.');
          this.changePasswordForm.reset();
        }

      }
    );
  }

  loadArchivedTickets(): void {
    this.ticketService.getTicketsArchivedOfUser(this.page, this.pageSize).subscribe((response) => {

      this.results = response.content.map(item => ({
        ...item,
        yourTicket: item.number.split('-'),
        yourVotes: item.votes,
        votedTicket: item.votedTicket?.number.split('-'),
        votedVotes: item.votedTicket?.votes
      }));

      this.pagination = {
        currentPage: this.page,
        totalPages: Math.ceil(response.pageInfo.totalItems / this.pageSize),
      }
    })
  }

}
